import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { LinearProgress, Grid, IconButton, Tooltip, Typography, List, ListItem, ListItemText } from '@mui/material'
import useAuth from '../../../hooks/useAuth'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { Stack } from '@mui/system'
import AdvisorApi from '../../../api/AdvisorApi'
import { AdvisorSelect } from '../../../components/AdvisorSelect'
import { FormIssueEntity } from '../types/FormIssueEntity'
import { FormConvertibleEntity } from '../types/FormConvertibleEntity'
import { AdvisorEntity, AdvisorType } from 'types/AdvisorEntity'

function AdvisorList({
    advisors,
    onDelete,
}: {
    advisors: Array<CustomizedAdvisorEntity>
    onDelete: (a: CustomizedAdvisorEntity) => void
}) {
    return (
        <List>
            {advisors
                .sort((a, b) => a?.name.localeCompare(b.name) || 0)
                .map((advisor) => (
                    <ListItem key={`${advisor.id}:${advisor.name}:${advisor.type}`}>
                        <Tooltip title='Ta bort'>
                            <IconButton size='small' onClick={() => onDelete(advisor)}>
                                <RemoveCircleOutlineIcon fontSize='inherit' />
                            </IconButton>
                        </Tooltip>
                        <ListItemText primary={advisor.name} />
                    </ListItem>
                ))}
        </List>
    )
}

function AddAdvisorFields({
    advisors,
    entityType,
    selectedAdvisors,
    onSelect,
    advisorType,
    setIsCustomFormDirty,
}: {
    advisors: Array<CustomizedAdvisorEntity> | null
    advisorType: AdvisorType
    entityType: 'issue' | 'convertible'
    onSelect: (a: CustomizedAdvisorEntity & { type: AdvisorType }) => void
    selectedAdvisors: Array<CustomizedAdvisorEntity>
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const checkboxName = {
        financial_advisor: 'noFinancialAdvisor',
        legal_advisor: 'noLegalAdvisor',
        issuing_agent: 'noIssuingAgent',
    }
    const formMethods = useFormContext()
    const [checked, setChecked] = useState(!!formMethods.getValues(checkboxName[advisorType]))
    const checkLabels = {
        financial_advisor: 'Inga finansiella rådgivare',
        legal_advisor: 'Inga legala rådgivare',
        issuing_agent: 'Inga emissionsinstitut',
    }
    const selectLabels = {
        financial_advisor: 'Ange ny finansiell rådgivare',
        legal_advisor: 'Ange ny juridisk rådgivare',
        issuing_agent: 'Ange nytt emissionsinstitut',
    }
    const handleSelect = (advisor: CustomizedAdvisorEntity) => onSelect({ ...advisor, type: advisorType })

    return (
        <Stack spacing={1}>
            <>
                {(!selectedAdvisors || selectedAdvisors.length === 0) && entityType !== 'convertible' && (
                    <RegisteredCheckBox
                        name={checkboxName[advisorType]}
                        label={checkLabels[advisorType]}
                        onChange={(event) => {
                            setChecked(event.target.checked)
                            if (setIsCustomFormDirty) {
                                setIsCustomFormDirty(true)
                            }
                        }}
                    />
                )}
            </>
            <>
                {!checked && (
                    <AdvisorSelect
                        advisors={advisors}
                        label={selectLabels[advisorType]}
                        onSelect={handleSelect}
                        onCreate={handleSelect}
                    />
                )}
            </>
        </Stack>
    )
}
const advisorApi = new AdvisorApi()

export type CustomizedAdvisorEntity = Omit<AdvisorEntity, 'advisorId'> & { id: string }

function AdvisorDetails({
    entity,
    entityType,
    setIsCustomFormDirty,
}: {
    entity: FormIssueEntity | FormConvertibleEntity
    entityType: 'issue' | 'convertible'
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const { isAuthenticated } = useAuth()
    const formMethods = useFormContext()
    const entityAdvisors = entity.advisors
    const [allAdvisors, setAllAdvisors] = useState<Array<CustomizedAdvisorEntity> | null>(null)
    const [categorizedAdvisors, setCategorizedAdvisors] = useState<{
        financialAdvisors: Array<CustomizedAdvisorEntity>
        legalAdvisors: Array<CustomizedAdvisorEntity>
        issuingAgents: Array<CustomizedAdvisorEntity>
    }>({
        financialAdvisors: [],
        legalAdvisors: [],
        issuingAgents: [],
    })
    const filterCategorizedAdvisors = useCallback(
        (issueAdvisors) => {
            const categorized = issueAdvisors.reduce(
                (acc, advisor) => {
                    if (advisor.type === 'financial_advisor') {
                        acc.financialAdvisors.push(advisor)
                    } else if (advisor.type === 'legal_advisor') {
                        acc.legalAdvisors.push(advisor)
                    } else if (advisor.type === 'issuing_agent' && entityType === 'issue') {
                        acc.issuingAgents.push(advisor)
                    } else {
                        throw new Error(`Unknown advisor type: ${advisor.type}`)
                    }
                    return acc
                },
                { financialAdvisors: [], legalAdvisors: [], issuingAgents: [] },
            )
            setCategorizedAdvisors(categorized)
            return categorized
        },
        [entityType],
    )
    useEffect(() => {
        if (!entity || !isAuthenticated) return
        filterCategorizedAdvisors(entity.advisors)
    }, [entity, isAuthenticated, formMethods.setValue, filterCategorizedAdvisors])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated) return
            const advs = await advisorApi.findAll()

            setAllAdvisors(
                advs
                    .map(({ advisorId, ...adv }) => ({ ...adv, id: advisorId }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
            )
        }
        fetchData()
    }, [formMethods, isAuthenticated])

    if (!entityAdvisors) return <LinearProgress />

    const handleAdd = (newAdvisor: CustomizedAdvisorEntity) => {
        // if the same advisor is already present (name, type combination), do not add it again
        if (entityAdvisors.find((a) => a.name === newAdvisor.name && a.type === newAdvisor.type)) return
        const newAdvisors = entityAdvisors.concat(newAdvisor)
        setIsCustomFormDirty(true)
        formMethods.setValue('advisors', newAdvisors)
        filterCategorizedAdvisors(newAdvisors)
    }
    const onDeleteAdvisor = (advisor: { id: string; type: AdvisorType; name: string }) => {
        const newAdvisors = entityAdvisors.filter((a) => {
            if (advisor.id) return !(a.id === advisor.id && a.type === advisor.type)
            else return !(a.name === advisor.name && a.type === advisor.type)
        })
        setIsCustomFormDirty(true)
        formMethods.setValue('advisors', newAdvisors)
        filterCategorizedAdvisors(newAdvisors)
    }
    const gridSize = entityType === 'issue' ? { xs: 12, md: 4 } : { xs: 12, md: 6 }

    return (
        <Grid container spacing={2}>
            <Grid size={gridSize}>
                <Typography variant='subtitle1'>Finansiella rådgivare</Typography>
                <AdvisorList advisors={categorizedAdvisors.financialAdvisors} onDelete={onDeleteAdvisor} />
                <AddAdvisorFields
                    advisors={allAdvisors}
                    entityType={entityType}
                    selectedAdvisors={categorizedAdvisors.financialAdvisors}
                    label='Ange ny finansiell rådgivare'
                    onSelect={handleAdd}
                    advisorType='financial_advisor'
                    setIsCustomFormDirty={setIsCustomFormDirty}
                />
            </Grid>
            <Grid size={gridSize}>
                <Typography variant='subtitle1'>Legala rådgivare</Typography>
                <AdvisorList advisors={categorizedAdvisors.legalAdvisors} onDelete={onDeleteAdvisor} />
                <AddAdvisorFields
                    advisors={allAdvisors}
                    entityType={entityType}
                    selectedAdvisors={categorizedAdvisors.legalAdvisors}
                    label='Ange ny juridisk rådgivare'
                    onSelect={handleAdd}
                    advisorType='legal_advisor'
                    setIsCustomFormDirty={setIsCustomFormDirty}
                />
            </Grid>
            {entityType === 'issue' ? (
                <Grid size={gridSize}>
                    <Typography variant='subtitle1'>Emissionsinstitut</Typography>
                    <AdvisorList advisors={categorizedAdvisors.issuingAgents} onDelete={onDeleteAdvisor} />
                    <AddAdvisorFields
                        advisors={allAdvisors}
                        entityType={entityType}
                        selectedAdvisors={categorizedAdvisors.issuingAgents}
                        label='Ange nytt emissionsinstitut'
                        onSelect={handleAdd}
                        advisorType='issuing_agent'
                        setIsCustomFormDirty={setIsCustomFormDirty}
                    />
                </Grid>
            ) : null}
        </Grid>
    )
}

export default AdvisorDetails
